<script setup>
import { computed, inject, ref } from "vue";
import { renderMarkdownInline } from "@utils/renderMarkdown";

const props = defineProps({
  data: { type: Array, required: true },
  donut: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
});

const emit = defineEmits(["bar-click"]);

// Chart scales in when the card is revealed on scroll (view mode entrance animation)
const revealed = inject("resultsCardRevealed", ref(true));

function onSliceClick(event, slice) {
  if (!props.clickable || slice.code === "999") return;
  event.stopPropagation();
  emit("bar-click", slice);
}

const size = 240;
const cx = size / 2;
const cy = size / 2;
const outerRadius = size / 2 - 4;
const innerRadius = computed(() => (props.donut ? outerRadius - 30 : 0));

function formatPercent(value) {
  return `${Math.round(value * 100)}%`;
}

const hoveredCode = ref(null);
const mousePos = ref({ x: 0, y: 0 });

function onSliceEnter(code, event) {
  hoveredCode.value = code;
  updatePos(event);
}

function updatePos(event) {
  const rect = event.currentTarget.closest("svg")?.getBoundingClientRect();
  if (!rect) return;
  mousePos.value = { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function onSliceLeave() {
  hoveredCode.value = null;
}

const hoveredSlice = computed(() => slices.value.find((s) => s.code === hoveredCode.value));

const slices = computed(() => {
  if (!props.data.length) return [];
  const total = props.data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return [];

  const result = [];
  let startAngle = -Math.PI / 2;

  props.data.forEach((d) => {
    const fraction = d.value / total;
    const endAngle = startAngle + fraction * 2 * Math.PI;
    const midAngle = (startAngle + endAngle) / 2;

    const r = outerRadius;
    const ir = innerRadius.value;

    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = fraction > 0.5 ? 1 : 0;

    let path;
    if (fraction >= 0.9999) {
      // A single 100% slice: an arc whose end point equals its start point is
      // degenerate and renders nothing, so draw a full circle (ring for a donut).
      path = `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
      if (ir > 0) {
        path += ` M ${cx} ${cy - ir} A ${ir} ${ir} 0 1 1 ${cx} ${cy + ir} A ${ir} ${ir} 0 1 1 ${cx} ${cy - ir} Z`;
      }
    } else if (ir > 0) {
      const ix1 = cx + ir * Math.cos(endAngle);
      const iy1 = cy + ir * Math.sin(endAngle);
      const ix2 = cx + ir * Math.cos(startAngle);
      const iy2 = cy + ir * Math.sin(startAngle);
      path = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${ir} ${ir} 0 ${largeArc} 0 ${ix2} ${iy2} Z`;
    } else {
      path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    }

    const labelR = ir > 0 ? (r + ir) / 2 : r * 0.6;
    const labelX = cx + labelR * Math.cos(midAngle);
    const labelY = cy + labelR * Math.sin(midAngle);

    result.push({
      ...d,
      path,
      fraction,
      labelX,
      labelY,
      showLabel: fraction >= 0.06,
    });

    startAngle = endAngle;
  });

  return result;
});
</script>

<template>
  <div v-if="data.length" class="flex flex-col items-center gap-2">
    <div
      class="relative transition-all duration-500 ease-out"
      :class="revealed ? 'opacity-100 scale-100' : 'opacity-0 scale-90'">
      <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" class="overflow-visible">
        <path
          v-for="slice in slices"
          :key="slice.code"
          :d="slice.path"
          :fill="slice.fill"
          fill-rule="evenodd"
          :opacity="hoveredCode && hoveredCode !== slice.code ? 0.5 : 1"
          class="transition-opacity duration-150 outline-none"
          :class="clickable && slice.code !== '999' ? 'cursor-pointer' : ''"
          stroke="white"
          stroke-width="1.5"
          @mouseenter="onSliceEnter(slice.code, $event)"
          @mousemove="updatePos"
          @mouseleave="onSliceLeave"
          @click="onSliceClick($event, slice)"
        />
        <text
          v-for="slice in slices"
          v-show="slice.showLabel"
          :key="`label-${slice.code}`"
          :x="slice.labelX"
          :y="slice.labelY"
          text-anchor="middle"
          dominant-baseline="central"
          class="pointer-events-none fill-white text-3xs font-medium"
          style="text-shadow: 0 1px 2px rgba(0,0,0,0.3)"
        >
          {{ formatPercent(slice.percentage) }}
        </text>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="hoveredSlice"
        class="pointer-events-none absolute z-10 border-border/50 bg-background text-foreground min-w-[10rem] rounded-lg border px-2.5 py-1.5 text-xs shadow-xl"
        :style="{ left: `${mousePos.x + 12}px`, top: `${mousePos.y - 8}px` }"
      >
        <div class="font-medium mb-1" v-html="renderMarkdownInline(hoveredSlice.label)" />
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-1.5">
            <div class="h-2.5 w-2.5 shrink-0 rounded-xs" :style="{ backgroundColor: hoveredSlice.fill }" />
            <span class="font-medium tabular-nums">
              {{ formatPercent(hoveredSlice.percentage) }}
              <span class="text-muted-foreground font-normal">(n={{ hoveredSlice.value }})</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs pt-1">
      <div
        v-for="slice in slices"
        :key="slice.code"
        class="flex items-center gap-1.5"
      >
        <div class="h-2 w-2 shrink-0 rounded-xs" :style="{ backgroundColor: slice.fill }" />
        <span class="text-muted-foreground" v-html="renderMarkdownInline(slice.label)" />
      </div>
    </div>
  </div>
</template>
